import React, { Component, createRef } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import axios from "../../../Actions/Axios";
import {
  CreatePDFTemplateInstanceAction,
  UpdatePDFTemplateInstanceAction,
  SetActivePDFTemplateAction,
} from "../../../Actions/Forms";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Loader2,
  AlertCircle,
  Pen,
  RotateCcw,
} from "lucide-react";

class PDFTemplateInstanceFill extends Component {
  static propTypes = {
    activePDFTemplate: PropTypes.object,
    pdfTemplateInstance: PropTypes.object,
    pdfTemplateInstanceID: PropTypes.number,
    CreatePDFTemplateInstanceAction: PropTypes.func.isRequired,
    UpdatePDFTemplateInstanceAction: PropTypes.func.isRequired,
    SetActivePDFTemplateAction: PropTypes.func.isRequired,
    navigate: PropTypes.func.isRequired,
    params: PropTypes.object,
  };

  state = {
    fields: [],
    allFields: [], // all fields including group_options for preview overlay
    currentFieldIdx: 0,
    formData: {},
    instanceId: null,
    creating: true,
    saving: false,
    error: null,
    completed: false,
    // PDF preview state
    previewPage: 1,
    previewImage: null,
    loadingPreview: false,
    templateId: null,
    // Mobile tab
    mobileTab: "form", // "form" | "preview"
    // Signature stroke width
    sigLineWidth: 2,
  };

  imgRef = createRef();
  sigCanvasRef = (node) => {
    this._sigCanvas = node;
    if (node) this.bindSigTouchListeners();
  };
  sigDrawing = false;
  sigLastX = 0;
  sigLastY = 0;
  sigStrokeWidth = 2;

  async componentDidMount() {
    let template = this.props.activePDFTemplate;
    const id = this.props.params?.id;

    if (!template && id) {
      try {
        const res = await axios.get(`/api/form/pdf-templates/${id}/`);
        template = res.data;
        this.props.SetActivePDFTemplateAction(template);
      } catch {
        this.props.navigate("/dashboard/smartflow");
        return;
      }
    }

    if (!template) {
      this.props.navigate("/dashboard/smartflow");
      return;
    }

    const allFields = template.fields || [];
    // Wizard fields: skip group_option (they are choices inside their group)
    const fields = allFields.filter((f) => f.type !== "group_option");
    const initialData = {};
    fields.forEach((f) => {
      if (f.type === "group" && f.selection === "multi") {
        initialData[f.name] = [];
      } else {
        initialData[f.name] = "";
      }
    });

    try {
      const instance = await this.props.CreatePDFTemplateInstanceAction(template.id);
      const firstPage = fields[0]?.page || 1;
      this.setState(
        {
          fields,
          allFields,
          formData: initialData,
          instanceId: instance.id,
          creating: false,
          previewPage: firstPage,
          templateId: template.id,
        },
        () => this.loadPreviewPage(firstPage)
      );
    } catch {
      this.setState({ creating: false, error: "Failed to create form session. Please try again." });
    }
  }

  loadPreviewPage = async (page) => {
    const { templateId } = this.state;
    if (!templateId) return;
    this.setState({ loadingPreview: true, previewImage: null });
    try {
      // API uses 0-based page index
      const res = await axios.get(`/api/form/pdf-templates/${templateId}/pages/${page - 1}/`);
      this.setState({ previewImage: res.data.image, previewPage: page, loadingPreview: false });
    } catch {
      this.setState({ loadingPreview: false });
    }
  };

  handleValueChange = (e) => {
    const { fields, currentFieldIdx } = this.state;
    const field = fields[currentFieldIdx];
    this.setState((prev) => ({
      formData: { ...prev.formData, [field.name]: e.target.value },
    }));
  };

  handleNext = async () => {
    const { fields, currentFieldIdx, instanceId, formData } = this.state;
    const field = fields[currentFieldIdx];

    // For signature fields, read directly from the canvas to avoid async state lag
    let fieldValue = formData[field.name];
    if (field.type === "signature") {
      fieldValue = this.getSigDataUrl() || fieldValue;
    }

    this.setState({ saving: true, error: null });
    try {
      await this.props.UpdatePDFTemplateInstanceAction(instanceId, {
        [field.name]: fieldValue,
      });
      if (currentFieldIdx < fields.length - 1) {
        const nextField = fields[currentFieldIdx + 1];
        const nextPage = nextField?.page || this.state.previewPage;
        const pageChanged = nextPage !== this.state.previewPage;
        // On mobile: briefly show the preview so the filled value is visible, then go back to form
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
          this.setState({ saving: false, mobileTab: "preview" }, () => {
            requestAnimationFrame(() => this.forceUpdate());
            setTimeout(() => {
              this.setState(
                { currentFieldIdx: currentFieldIdx + 1, mobileTab: "form" },
                () => { if (pageChanged) this.loadPreviewPage(nextPage); }
              );
            }, 800);
          });
        } else {
          this.setState(
            { currentFieldIdx: currentFieldIdx + 1, saving: false },
            () => { if (pageChanged) this.loadPreviewPage(nextPage); }
          );
        }
      } else {
        this.setState({ saving: false, completed: true });
      }
    } catch {
      this.setState({ saving: false, error: "Failed to save. Please try again." });
    }
  };

  handleBack = () => {
    this.setState((prev) => {
      const newIdx = Math.max(0, prev.currentFieldIdx - 1);
      const prevField = prev.fields[newIdx];
      const prevPage = prevField?.page || prev.previewPage;
      const pageChanged = prevPage !== prev.previewPage;
      return { currentFieldIdx: newIdx };
    }, () => {
      const { fields, currentFieldIdx, previewPage } = this.state;
      const field = fields[currentFieldIdx];
      if (field?.page && field.page !== previewPage) {
        this.loadPreviewPage(field.page);
      }
    });
  };

  // Returns all field boxes (including group_options) for the current preview page
  getPreviewBoxes = () => {
    const { allFields, previewPage } = this.state;
    return allFields.filter(
      (f) => f.page === previewPage && f.type !== "group" && f.x != null
    );
  };

  // Get the field name a group_option belongs to (via its groupId → group's name)
  getGroupName = (groupOptionField) => {
    const { allFields } = this.state;
    const group = allFields.find((f) => f.id === groupOptionField.groupId && f.type === "group");
    return group?.name || null;
  };

  // Determine what label to show inside a field box on the preview
  getFieldPreviewValue = (field) => {
    const { formData, fields, currentFieldIdx } = this.state;

    if (field.type === "group_option") {
      const groupName = this.getGroupName(field);
      if (!groupName) return null;
      const groupValue = formData[groupName];
      if (!groupValue) return null;
      const group = this.state.allFields.find((f) => f.type === "group" && f.name === groupName);
      const opt = (group?.options || []).find((o) => o.fieldId === field.id);
      if (!opt) return null;
      if (Array.isArray(groupValue)) {
        return groupValue.includes(opt.label) ? "✓" : null;
      }
      return groupValue === opt.label ? "✓" : null;
    }

    const raw = formData[field.name];
    if (!raw) return null;

    if (field.type === "signature") return "__signature__";

    // For date fields, reformat ISO value to match the form's date_format (e.g. DDMMYYYY)
    if (field.type === "date") {
      const fmt = field.date_format || "DDMMYYYY";
      const parts = raw.split("-");
      if (parts.length === 3) {
        const year = parts[0], month = parts[1], day = parts[2];
        let out = fmt;
        out = out.replace("YYYY", year).replace("YY", year.slice(-2));
        out = out.replace("MM", month).replace("DD", day);
        return out;
      }
    }

    return raw;
  };

  // Determine the visual state of a preview box
  getBoxState = (field) => {
    const { fields, currentFieldIdx, allFields } = this.state;

    // Active: the wizard field currently on screen
    const activeField = fields[currentFieldIdx];
    if (!activeField) return "empty";

    if (field.type === "group_option") {
      // Active if the active wizard field is the parent group
      const groupName = this.getGroupName(field);
      if (activeField.type === "group" && activeField.name === groupName) return "active";
      // Filled if it has a checked value
      const val = this.getFieldPreviewValue(field);
      if (val) return "filled";
      return "empty";
    }

    if (field.id === activeField.id) return "active";
    const val = this.getFieldPreviewValue(field);
    if (val) return "filled";

    // Upcoming?
    const wizardIdx = fields.findIndex((f) => f.id === field.id);
    if (wizardIdx > currentFieldIdx) return "upcoming";

    return "empty";
  };

  // Attach non-passive touch listeners directly so we can call preventDefault
  // (React synthetic touch events are passive by default and can't prevent scroll)
  bindSigTouchListeners = () => {
    const canvas = this._sigCanvas;
    if (!canvas) return;
    canvas.removeEventListener("touchstart", this._sigTouchStart);
    canvas.removeEventListener("touchmove", this._sigTouchMove);
    canvas.removeEventListener("touchend", this._sigTouchEnd);
    this._sigTouchStart = (e) => { e.preventDefault(); this.sigStart(e); };
    this._sigTouchMove = (e) => { e.preventDefault(); this.sigMove(e); };
    this._sigTouchEnd = (e) => { e.preventDefault(); this.sigEnd(); };
    canvas.addEventListener("touchstart", this._sigTouchStart, { passive: false });
    canvas.addEventListener("touchmove", this._sigTouchMove, { passive: false });
    canvas.addEventListener("touchend", this._sigTouchEnd, { passive: false });
  };

getSigPoint = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * (canvas.width / rect.width),
      y: (src.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  sigStart = (e) => {
    const canvas = this._sigCanvas;
    if (!canvas) return;
    this.sigDrawing = true;
    const { x, y } = this.getSigPoint(e, canvas);
    this.sigLastX = x;
    this.sigLastY = y;
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.arc(x, y, 1, 0, Math.PI * 2);
    ctx.fillStyle = "#0f172a";
    ctx.fill();
  };

  sigMove = (e) => {
    if (!this.sigDrawing) return;
    const canvas = this._sigCanvas;
    if (!canvas) return;
    const { x, y } = this.getSigPoint(e, canvas);
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(this.sigLastX, this.sigLastY);
    ctx.lineTo(x, y);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = this.sigStrokeWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    this.sigLastX = x;
    this.sigLastY = y;
  };

  sigEnd = () => {
    if (!this.sigDrawing) return;
    this.sigDrawing = false;
    const canvas = this._sigCanvas;
    if (!canvas) return;
    const { fields, currentFieldIdx } = this.state;
    const field = fields[currentFieldIdx];
    const dataUrl = canvas.toDataURL("image/png");
    this.setState((prev) => ({
      formData: { ...prev.formData, [field.name]: dataUrl },
    }));
  };

  // Read the current canvas state — used by handleNext to guarantee latest value
  getSigDataUrl = () => {
    const canvas = this._sigCanvas;
    if (!canvas) return null;
    return canvas.toDataURL("image/png");
  };

  sigClear = () => {
    const canvas = this._sigCanvas;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const { fields, currentFieldIdx } = this.state;
    const field = fields[currentFieldIdx];
    this.setState((prev) => ({
      formData: { ...prev.formData, [field.name]: "" },
    }));
  };

  renderInput = () => {
    const { fields, currentFieldIdx, formData } = this.state;
    const field = fields[currentFieldIdx];
    const value = formData[field.name] ?? "";
    const baseClass =
      "w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm font-inter text-gray-900 placeholder-gray-400 outline-none focus:bg-white focus:border-blue-500 transition-all";

    if (field.type === "group") {
      const options = field.options || [];
      if (field.selection === "single") {
        return (
          <div className="flex flex-col gap-2">
            {options.map((opt) => (
              <button
                key={opt.fieldId}
                type="button"
                onClick={() =>
                  this.setState((prev) => ({
                    formData: { ...prev.formData, [field.name]: opt.label },
                  }))
                }
                className={`w-full py-3 px-4 rounded-xl font-inter font-medium text-sm text-left transition-all border ${
                  value === opt.label
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        );
      }
      const selected = Array.isArray(value) ? value : [];
      return (
        <div className="flex flex-col gap-2">
          {options.map((opt) => {
            const isChecked = selected.includes(opt.label);
            return (
              <button
                key={opt.fieldId}
                type="button"
                onClick={() => {
                  const next = isChecked
                    ? selected.filter((v) => v !== opt.label)
                    : [...selected, opt.label];
                  this.setState((prev) => ({
                    formData: { ...prev.formData, [field.name]: next },
                  }));
                }}
                className={`w-full py-3 px-4 rounded-xl font-inter font-medium text-sm text-left transition-all border ${
                  isChecked
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      );
    }
    if (field.type === "date") {
      const dateFormat = field.date_format || "";
      const formatHint = dateFormat
        ? dateFormat.replace(/DD/g, "DD").replace(/MM/g, "MM").replace(/YYYY/g, "YYYY").replace(/YY/g, "YY")
        : null;
      return (
        <div className="flex flex-col gap-2">
          <input
            type="date"
            value={value}
            onChange={this.handleValueChange}
            className={baseClass}
          />
          {formatHint && (
            <p className="text-xs text-gray-400 font-inter pl-1">
              This will be placed on the form as: <span className="font-mono font-medium text-gray-500">{formatHint}</span>
            </p>
          )}
        </div>
      );
    }
    if (field.type === "boolean") {
      return (
        <div className="flex gap-3">
          {["Yes", "No"].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() =>
                this.setState((prev) => ({
                  formData: { ...prev.formData, [field.name]: opt },
                }))
              }
              className={`flex-1 py-3 rounded-xl font-inter font-semibold text-sm transition-all border ${
                value === opt
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      );
    }
    if (field.type === "number") {
      return (
        <input
          type="number"
          value={value}
          onChange={this.handleValueChange}
          placeholder="Enter a number"
          className={baseClass}
        />
      );
    }
    if (field.type === "signature") {
      const { sigLineWidth } = this.state;
      return (
        <div className="flex flex-col gap-3">
          <div className="relative rounded-xl overflow-hidden border-2 border-dashed border-gray-300 bg-gray-50 touch-none"
            style={{ height: 180 }}>
            {!value && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none">
                <Pen className="w-6 h-6 text-gray-300" />
                <span className="font-inter text-xs text-gray-300">Sign here with your finger or mouse</span>
              </div>
            )}
            <canvas
              ref={this.sigCanvasRef}
              width={900}
              height={270}
              className="w-full h-full"
              style={{ touchAction: "none" }}
              onMouseDown={this.sigStart}
              onMouseMove={this.sigMove}
              onMouseUp={this.sigEnd}
              onMouseLeave={this.sigEnd}
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1">
              <span className="font-inter text-xs text-gray-400 shrink-0">Thickness</span>
              <input
                type="range"
                min={1}
                max={8}
                step={0.5}
                value={sigLineWidth}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  this.sigStrokeWidth = v;
                  this.setState({ sigLineWidth: v });
                }}
                className="flex-1 accent-blue-600"
              />
              <span
                className="shrink-0 flex items-center justify-center"
                style={{ width: 24, height: 24 }}
              >
                <span
                  className="rounded-full bg-gray-800 inline-block"
                  style={{ width: sigLineWidth * 2.5, height: sigLineWidth * 2.5 }}
                />
              </span>
            </div>
            <button
              type="button"
              onClick={this.sigClear}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-inter text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shrink-0"
            >
              <RotateCcw className="w-3 h-3" /> Clear
            </button>
          </div>
        </div>
      );
    }
    return (
      <input
        type="text"
        value={value}
        onChange={this.handleValueChange}
        placeholder={`Enter ${field.label || field.name.replace(/_/g, " ")}`}
        className={baseClass}
        onKeyDown={(e) => e.key === "Enter" && this.handleNext()}
      />
    );
  };

  renderPreviewOverlay = () => {
    if (!this.imgRef.current) return null;
    const img = this.imgRef.current;
    // Use offsetWidth/offsetHeight which work even after tab switch,
    // falling back to getBoundingClientRect if offset dimensions are zero
    let w = img.offsetWidth;
    let h = img.offsetHeight;
    if (!w || !h) {
      const rect = img.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
    }
    // If still zero (image not yet laid out), derive height from natural aspect ratio
    if (!h && img.naturalWidth && img.naturalHeight && w) {
      h = w * (img.naturalHeight / img.naturalWidth);
    }
    if (!w || !h) return null;
    const boxes = this.getPreviewBoxes();

    return boxes.map((field) => {
      const state = this.getBoxState(field);
      const value = this.getFieldPreviewValue(field);

      let borderColor, bgColor, textColor;
      if (state === "active") {
        borderColor = "border-blue-500";
        bgColor = "bg-blue-400/15";
        textColor = "text-blue-700";
      } else if (state === "filled") {
        borderColor = "border-emerald-500";
        bgColor = "bg-emerald-400/15";
        textColor = "text-emerald-800";
      } else if (state === "upcoming") {
        borderColor = "border-gray-300";
        bgColor = "bg-gray-100/30";
        textColor = "text-gray-400";
      } else {
        borderColor = "border-gray-200";
        bgColor = "bg-transparent";
        textColor = "text-gray-300";
      }

      const isSignature = field.type === "signature";
      const sigDataUrl = isSignature ? this.state.formData[field.name] : null;
      const displayValue = isSignature ? null : (value || (state === "active" ? "…" : ""));
      const cellCount = field.cell_count || 0;
      const boxW = field.width * w;
      const boxH = field.height * h;

      return (
        <div
          key={field.id}
          className={`absolute border-2 ${borderColor} ${bgColor} overflow-hidden pointer-events-none transition-all duration-300`}
          style={{
            left: field.x * w,
            top: field.y * h,
            width: boxW,
            height: boxH,
          }}
        >
          {isSignature && sigDataUrl ? (
            <img
              src={sigDataUrl}
              alt="signature"
              className="absolute inset-0 w-full h-full object-contain"
              style={{ mixBlendMode: "multiply" }}
            />
          ) : displayValue && cellCount > 1 ? (
            // Cell-by-cell rendering: one character per equally-spaced slot
            <div className="absolute inset-0 flex">
              {Array.from({ length: cellCount }).map((_, ci) => (
                <div
                  key={ci}
                  className={`flex-1 flex items-center justify-center text-[9px] font-mono font-bold ${textColor}`}
                  style={{ borderRight: ci < cellCount - 1 ? "1px solid rgba(0,0,0,0.08)" : "none" }}
                >
                  {displayValue[ci] || ""}
                </div>
              ))}
            </div>
          ) : displayValue ? (
            <span
              className={`absolute inset-0 flex items-center px-1 text-[10px] font-inter font-semibold ${textColor} truncate`}
            >
              {displayValue}
            </span>
          ) : null}
          {state === "active" && (
            <span className="absolute top-0 right-0 w-1.5 h-full bg-blue-500 animate-pulse" />
          )}
        </div>
      );
    });
  };

  render() {
    const {
      fields,
      currentFieldIdx,
      creating,
      saving,
      error,
      completed,
      instanceId,
      previewImage,
      loadingPreview,
      previewPage,
    } = this.state;
    const { activePDFTemplate } = this.props;

    if (creating) {
      return (
        <div className="w-full min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      );
    }

    if (error && !fields.length) {
      return (
        <div className="w-full min-h-screen flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="font-inter text-gray-600">{error}</p>
            <Link
              to="/dashboard/smartflow"
              className="mt-4 inline-block text-blue-500 text-sm font-inter hover:underline"
            >
              Back to templates
            </Link>
          </div>
        </div>
      );
    }

    if (completed) {
      return (
        <div className="w-full min-h-screen flex items-center justify-center">
          <div className="text-center max-w-sm mx-auto px-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="font-SFPro font-bold text-xl text-gray-900 mb-2">All done!</h2>
            <p className="font-inter text-sm text-gray-400 mb-6">
              All fields have been saved. You can now generate your filled PDF.
            </p>
            <button
              onClick={() =>
                this.props.navigate(
                  `/dashboard/smartflow/instance/${instanceId}/preview`
                )
              }
              className="w-full py-3 bg-blue-600 text-white font-inter font-semibold text-sm rounded-xl hover:bg-blue-700 transition-colors"
            >
              Preview & Download PDF
            </button>
            <Link
              to="/dashboard/smartflow"
              className="block mt-3 text-center font-inter text-sm text-gray-400 hover:text-gray-600"
            >
              Back to templates
            </Link>
          </div>
        </div>
      );
    }

    if (!fields.length) {
      return (
        <div className="w-full min-h-screen flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <p className="font-inter text-gray-600 mb-3">
              This template has no fields mapped yet.
            </p>
            <Link
              to={`/dashboard/smartflow/${activePDFTemplate?.id}/map`}
              className="inline-block px-4 py-2 bg-blue-600 text-white font-inter text-sm rounded-xl hover:bg-blue-700 transition-colors"
            >
              Map Fields
            </Link>
          </div>
        </div>
      );
    }

    const field = fields[currentFieldIdx];
    const progress = Math.round(((currentFieldIdx + 1) / fields.length) * 100);
    const { mobileTab } = this.state;

    return (
      <div className="w-full h-screen bg-gray-50 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 md:px-6 py-3 flex items-center gap-3 md:gap-4">
          <button
            onClick={() => this.props.navigate("/dashboard/smartflow")}
            className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="font-inter text-xs text-gray-400 uppercase tracking-wide truncate">
              {activePDFTemplate?.name}
            </p>
            <div className="mt-1 w-full bg-gray-100 rounded-full h-1.5">
              <div
                className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          <span className="font-inter text-xs text-gray-400 shrink-0">
            {currentFieldIdx + 1}/{fields.length}
          </span>
        </div>

        {/* Mobile tab switcher — only visible on small screens */}
        <div className="md:hidden flex border-b border-gray-200 bg-white">
          <button
            onClick={() => this.setState({ mobileTab: "form" })}
            className={`flex-1 py-2.5 text-sm font-inter font-medium transition-colors ${
              mobileTab === "form"
                ? "text-blue-600 border-b-2 border-blue-500"
                : "text-gray-400"
            }`}
          >
            Fill Form
          </button>
          <button
            onClick={() => this.setState({ mobileTab: "preview" }, () => {
              // Re-render overlay after the preview panel becomes visible
              requestAnimationFrame(() => this.forceUpdate());
            })}
            className={`flex-1 py-2.5 text-sm font-inter font-medium transition-colors ${
              mobileTab === "preview"
                ? "text-blue-600 border-b-2 border-blue-500"
                : "text-gray-400"
            }`}
          >
            Preview
          </button>
        </div>

        {/* Two-column layout: centered card side-by-side on desktop, stacked on mobile */}
        <div className="flex flex-1 min-h-0 items-stretch justify-center md:py-6 md:px-4 overflow-hidden">
          <div className="flex flex-col md:flex-row w-full max-w-5xl md:shadow-xl md:rounded-2xl overflow-hidden bg-white">
          {/* Left: Form wizard — full width on mobile, fixed width on desktop */}
          <div className={`${mobileTab === "preview" ? "hidden md:flex" : "flex"} md:w-[400px] w-full shrink-0 flex-col items-center overflow-y-auto px-5 md:px-8 py-8 md:py-10 border-b md:border-b-0 md:border-r border-gray-200 bg-white`}>
            <div className="w-full max-w-sm">
              {/* Question card */}
              <div className="mb-2">
                <h2 className="font-SFPro font-bold text-xl text-gray-900 mb-1 capitalize">
                  {field.label || field.name.replace(/_/g, " ")}
                </h2>
                <p className="font-inter text-sm text-gray-400 mb-6">
                  {field.type === "group"
                    ? `Select ${field.selection === "single" ? "one option" : "one or more options"}`
                    : `Type: ${field.type}`}
                </p>
              </div>

              {this.renderInput()}

              {error && (
                <div className="flex items-center gap-2 mt-4 text-red-500 font-inter text-sm">
                  <AlertCircle className="w-4 h-4" /> {error}
                </div>
              )}

              <div className="flex gap-3 mt-6">
                {currentFieldIdx > 0 && (
                  <button
                    onClick={this.handleBack}
                    className="flex-1 py-3 border border-gray-200 text-gray-600 font-inter font-medium text-sm rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                )}
                <button
                  onClick={this.handleNext}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-600 text-white font-inter font-semibold text-sm rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                  ) : currentFieldIdx < fields.length - 1 ? (
                    <>Next <ArrowRight className="w-4 h-4" /></>
                  ) : (
                    <>Finish <CheckCircle className="w-4 h-4" /></>
                  )}
                </button>
              </div>

              {/* Field list summary */}
              <div className="mt-8 flex flex-col gap-1.5">
                {fields.map((f, idx) => {
                  const val = this.state.formData[f.name];
                  const hasValue = Array.isArray(val) ? val.length > 0 : !!val;
                  const isActive = idx === currentFieldIdx;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => {
                        const targetPage = f.page || this.state.previewPage;
                        const pageChanged = targetPage !== this.state.previewPage;
                        this.setState({ currentFieldIdx: idx }, () => {
                          if (pageChanged) this.loadPreviewPage(targetPage);
                        });
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-left ${
                        isActive ? "bg-blue-50 border border-blue-200" : "bg-transparent hover:bg-gray-50"
                      }`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          isActive
                            ? "bg-blue-500"
                            : hasValue
                            ? "bg-emerald-500"
                            : "bg-gray-200"
                        }`}
                      />
                      <span
                        className={`font-inter text-xs truncate ${
                          isActive ? "text-blue-700 font-semibold" : hasValue ? "text-gray-700" : "text-gray-400"
                        }`}
                      >
                        {f.label || f.name.replace(/_/g, " ")}
                      </span>
                      {hasValue && !isActive && (
                        <span className="ml-auto font-inter text-xs text-gray-500 truncate max-w-[90px]">
                          {Array.isArray(val) ? val.join(", ") : val}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: Live PDF preview */}
          <div className={`${mobileTab === "form" ? "hidden md:flex" : "flex"} flex-1 flex-col items-center justify-start overflow-y-auto bg-gray-100 p-4 md:p-6 gap-3`}>
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-3 md:gap-4 self-start bg-white border border-gray-200 rounded-xl px-3 md:px-4 py-2 text-xs font-inter text-gray-500">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded border-2 border-blue-500 bg-blue-400/15 inline-block" />
                Current field
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded border-2 border-emerald-500 bg-emerald-400/15 inline-block" />
                Filled
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded border-2 border-gray-300 bg-gray-100/30 inline-block" />
                Upcoming
              </span>
              <span className="font-inter text-gray-400">Page {previewPage}</span>
            </div>

            {/* PDF image with field overlays */}
            <div className="relative inline-block shadow-xl rounded-lg overflow-visible w-full">
              {loadingPreview ? (
                <div className="w-full h-[60vw] md:h-[760px] bg-white rounded-lg flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-gray-300 animate-spin" />
                </div>
              ) : previewImage ? (
                <>
                  <img
                    ref={this.imgRef}
                    src={`data:image/png;base64,${previewImage}`}
                    alt={`Page ${previewPage}`}
                    className="block w-full h-auto rounded-lg"
                    onLoad={() => this.forceUpdate()}
                    draggable={false}
                  />
                  {this.renderPreviewOverlay()}
                </>
              ) : (
                <div className="w-full h-[60vw] md:h-[760px] bg-white rounded-lg flex items-center justify-center text-gray-300 font-inter text-sm">
                  No preview available
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  activePDFTemplate: state.forms.activePDFTemplate,
  pdfTemplateInstance: state.forms.pdfTemplateInstance,
  pdfTemplateInstanceID: state.forms.pdfTemplateInstanceID,
});

export default withRouterHooks(
  connect(mapStateToProps, {
    CreatePDFTemplateInstanceAction,
    UpdatePDFTemplateInstanceAction,
    SetActivePDFTemplateAction,
  })(PDFTemplateInstanceFill)
);
